import type FeatureLayer from '@arcgis/core/layers/FeatureLayer.js';
import { findLayer } from '@/map/layers/registry';
import { getOrCreateFeatureLayer } from '@/map/layers/factory';
import type { LayerProbe, LayerRecord } from '@/map/layers/types';

export async function ensureFeatureLayer(layerId: string): Promise<FeatureLayer> {
  const record = findLayer(layerId);
  if (!record) throw new Error(`Unknown layer id: "${layerId}"`);
  const layer = getOrCreateFeatureLayer(record);
  await layer.load().catch(() => undefined);
  return layer;
}

export function requireLayerRecord(layerId: string): LayerRecord {
  const record = findLayer(layerId);
  if (!record) throw new Error(`Unknown layer id: "${layerId}"`);
  return record;
}

export function translateAttributes(
  attributes: Record<string, unknown>,
  probe: LayerProbe | undefined,
): Record<string, unknown> {
  if (!probe) return { ...attributes };
  const out: Record<string, unknown> = {};
  const codedByField = new Map<string, Record<string, string>>();
  for (const f of probe.fields) {
    if (f.coded_values) codedByField.set(f.name, f.coded_values);
  }
  for (const [k, v] of Object.entries(attributes)) {
    const map = codedByField.get(k);
    if (map && v != null) {
      const key = String(v);
      out[k] = map[key] ?? v;
    } else {
      out[k] = v;
    }
  }
  return out;
}

function normalizeToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function resolveFieldName(
  requested: string | undefined,
  probe: LayerProbe | undefined,
  layer: FeatureLayer,
): string | undefined {
  if (!requested) return undefined;
  const token = requested.trim();
  if (!token) return undefined;

  const probeFields = probe?.fields ?? [];
  const layerFields = (layer.fields ?? []).map((f) => ({ name: f.name, alias: f.alias }));
  const candidates = [...probeFields, ...layerFields];
  if (candidates.length === 0) return token;

  const exactName = candidates.find((f) => f.name === token)?.name;
  if (exactName) return exactName;

  const ciName = candidates.find((f) => f.name.toLowerCase() === token.toLowerCase())?.name;
  if (ciName) return ciName;

  const exactAlias = candidates.find((f) => f.alias === token)?.name;
  if (exactAlias) return exactAlias;

  const ciAlias = candidates.find((f) => (f.alias ?? '').toLowerCase() === token.toLowerCase())?.name;
  if (ciAlias) return ciAlias;

  const normalized = normalizeToken(token);
  const softName = candidates.find((f) => normalizeToken(f.name) === normalized)?.name;
  if (softName) return softName;

  const softAlias = candidates.find((f) => normalizeToken(f.alias ?? '') === normalized)?.name;
  if (softAlias) return softAlias;

  return undefined;
}

export function resolveFieldList(
  requested: string[] | undefined,
  probe: LayerProbe | undefined,
  layer: FeatureLayer,
): string[] | undefined {
  if (!requested) return undefined;
  const resolved: string[] = [];
  for (const field of requested) {
    if (field === '*') {
      resolved.push(field);
      continue;
    }
    const match = resolveFieldName(field, probe, layer);
    resolved.push(match ?? field);
  }
  return resolved;
}
